import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import times from 'lodash/times';

import {
  Box,
  List,
  Skeleton,
  styled,
  usePreferenceContext,
} from '@sbv-clients/ui-shared';

import { SideBarMenuItem } from '../types';
import SidebarNavSection from './sidebar-nav-section';
import SidebarNavSectionWithChildren from './sidebar-nav-section-with-childrens';

const SideMenu = styled(Box)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.text?.secondary};
  flex-grow: 1;
`;

const SideBarItems = ({
  items,
  fromRouteLabel,
  setFromRouteLabel,
  itemsLoading,
}: {
  items: SideBarMenuItem[];
  fromRouteLabel: string;
  setFromRouteLabel: Dispatch<SetStateAction<string>>;
  itemsLoading?: boolean;
}) => {
  const { setSidebarMinimized } = usePreferenceContext();
  const router = useLocation();
  const currentRoute = router.pathname;
  if (itemsLoading) {
    return (
      <Box display="grid" gridTemplateColumns="1fr" rowGap={5} p={1}>
        {times(5, (index) => {
          return (
            <Skeleton key={index} variant="rounded" width="100%" height={30} />
          );
        })}
      </Box>
    );
  }

  return (
    <>
      {items.map((item) => {
        const { id, link } = item;
        const children = get(item, 'children', []);
        if (children.length === 0) {
          const active =
            link === currentRoute ||
            (currentRoute.startsWith(link as string) && link !== '/');
          return (
            <SidebarNavSection key={id} item={item} active={active} withLink />
          );
        } else {
          const childrenItems = keyBy(children, 'link');
          const active = fromRouteLabel
            ? item.label === fromRouteLabel
            : !!childrenItems[currentRoute];
          return (
            <SidebarNavSectionWithChildren
              key={id}
              item={item}
              active={
                (active && !fromRouteLabel) || item.label === fromRouteLabel
              }
              onSelectChildRoute={() => {
                if (fromRouteLabel) {
                  setSidebarMinimized(true);
                  setFromRouteLabel('');
                }
              }}
            />
          );
        }
      })}
    </>
  );
};

const MinimisedSideBarItems = ({
  items,
  setFromRouteLabel,
  itemsLoading,
}: {
  items: SideBarMenuItem[];
  setFromRouteLabel: Dispatch<SetStateAction<string>>;
  itemsLoading?: boolean;
}) => {
  const { setSidebarMinimized } = usePreferenceContext();
  const router = useLocation();
  const currentRoute = router.pathname;

  const getIsActive = useCallback(
    (item: SideBarMenuItem) => {
      const { link = '' } = item;
      const children = get(item, 'children', []);
      if (children.length === 0) {
        return (
          link === currentRoute ||
          (currentRoute.startsWith(link as string) && link !== '/')
        );
      } else {
        return children.some((child) =>
          currentRoute.startsWith(child.link ?? '')
        );
      }
    },
    [currentRoute]
  );

  if (itemsLoading) {
    return (
      <Box display="grid" gridTemplateColumns="1fr" rowGap={5} p={1}>
        {times(5, (index: number) => {
          return (
            <Skeleton key={index} variant="rounded" width="100%" height={30} />
          );
        })}
      </Box>
    );
  }
  return (
    <>
      {items.map((item) => {
        const { id } = item;
        const active = getIsActive(item);
        const itemWithNoChildren = isEmpty(item.children);
        return (
          <SidebarNavSection
            key={id}
            item={item}
            active={active}
            withLink={itemWithNoChildren}
            showLabel={false}
            onClick={() => {
              if (!itemWithNoChildren) {
                setFromRouteLabel(item.label ?? '');
                return setSidebarMinimized(false);
              }
            }}
          />
        );
      })}
    </>
  );
};

const SidebarNav = ({
  items,
  isMinimisedView,
  itemsLoading,
}: {
  items: SideBarMenuItem[];
  isMinimisedView?: boolean;
  itemsLoading?: boolean;
}) => {
  const [fromRouteLabel, setFromRouteLabel] = useState<string>('');
  return (
    <SideMenu>
      <List>
        <Box>
          {isMinimisedView ? (
            <MinimisedSideBarItems
              itemsLoading={itemsLoading}
              items={items}
              setFromRouteLabel={setFromRouteLabel}
            />
          ) : (
            <SideBarItems
              itemsLoading={itemsLoading}
              items={items}
              fromRouteLabel={fromRouteLabel}
              setFromRouteLabel={setFromRouteLabel}
            />
          )}
        </Box>
      </List>
    </SideMenu>
  );
};

export default SidebarNav;
